import React from "react";

import { COLORS } from "shared/config/constants";

/**
 * The canvas draws every panel glyph inline with exact geometry — stroke-based,
 * sized to the marker they sit in. The shared icon set is fill-based and needed
 * per-site CSS coercion to approximate them; these are the drawings themselves,
 * kept in one file so every marker renders from the same path data.
 */

/** Verdict tick (canvas .ico ok): 13px stroke tick inside the 22px row disc;
 *  the Lumen summary block draws it 12px at 3.2 in a 20px disc (§7.1). */
export const TickGlyph = ({
  size = 13,
  strokeWidth = 3,
}: {
  size?: number;
  strokeWidth?: number;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 13l4 4L19 7"
      stroke={COLORS.accentDark}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Verdict cross (canvas .ico bad): 11px stroke cross inside the 22px disc. */
export const CrossGlyph = ({
  size = 11,
  strokeWidth = 3,
}: {
  size?: number;
  strokeWidth?: number;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke={COLORS.failedRed}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
    />
  </svg>
);

/** Conditional bang — the amber chip family's mark, disc-sized like the
 *  tick and cross. Not drawn on the canvas (the summary block is drawn
 *  approve-only); it borrows the circle-alert's bang path. */
export const BangGlyph = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 6v7M12 17.5h.01"
      stroke="#b35500"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

/** Row edit affordance: the canvas pencil, stroked in the button's color. */
export const PencilGlyph = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

export const SpinnerGlyph = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={COLORS.secondary}
    strokeWidth="2.4"
    strokeLinecap="round"
    aria-hidden
    style={{ animation: "vero1-spin 0.7s linear infinite" }}
  >
    <path d="M12 3a9 9 0 1 0 9 9" />
    <style>{`@keyframes vero1-spin { to { transform: rotate(360deg); } }`}</style>
  </svg>
);

export const TrashGlyph = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/** Internal-note lock — the row eyebrow draws it 9px/2.4, the editor label
 *  11px/1.9 with a softer corner; both are the same padlock. */
export const LockGlyph = ({
  size = 9,
  strokeWidth = 2.4,
  cornerRadius = 2,
}: {
  size?: number;
  strokeWidth?: number;
  cornerRadius?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="5" y="11" width="14" height="10" rx={cornerRadius} />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/** Conflict marker on both rejected-save surfaces (row editor and submit);
 *  the same mark in amber warns about a company-wide scope (canvas 10.3). */
export const CircleAlertGlyph = ({
  size = 15,
  tone = COLORS.failedRed,
}: {
  size?: number;
  tone?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={tone}
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4.5M12 16h.01" />
  </svg>
);

/** Minimize control: the canvas chevron pointing at the rail. */
export const ChevronRightGlyph = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/** The rail's expand chevron — the same mark reversed (canvas 6.2). */
export const ChevronLeftGlyph = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M15 6l-6 6 6 6" />
  </svg>
);
